<template>
  <div class="login-container">
    <el-card class="auth-form">
      <template #header>
        <div class="card-header">
          <h2>Добро пожаловать в МНОС!</h2>
          <div class="exchange-rates">
            <el-divider>
              <span class="divider-text">Курсы валют</span>
            </el-divider>
            <div v-if="loadingRates" class="rates-loading">
              <el-skeleton :rows="2" animated />
            </div>
            <div v-else-if="exchangeRates.length > 0" class="rates-grid">
              <div v-for="rate in exchangeRates" :key="rate.currency" class="rate-item">
                <img :src="getCurrencyFlagUrl(rate.currency)" :alt="rate.currency" class="currency-flag" />
                <span class="currency-code">{{ rate.currency }}</span>
                <span class="rate-value">{{ rate.rate.toFixed(4) }}</span>
              </div>
            </div>
          </div>
          <p class="subtitle">{{ isLogin ? 'Пожалуйста, войдите в аккаунт' : 'Пожалуйста, заполните данные для создания аккаунта' }}</p>
        </div>
      </template>

      <el-form
        ref="formRef"
        :model="form"
        label-position="top"
        @submit.prevent="handleSubmit"
      >
        <el-form-item label="Email" prop="email">
          <el-input
            v-model="form.email"
            type="email"
            placeholder="Введите ваш email"
            :prefix-icon="Message"
          />
        </el-form-item>

        <el-form-item label="Пароль" prop="password">
          <el-input
            v-model="form.password"
            type="password"
            placeholder="Введите ваш пароль"
            :prefix-icon="Lock"
            show-password
          />
        </el-form-item>

        <el-form-item v-if="!isLogin" label="Идентификатор менеджера" prop="sales_person_id">
          <el-select
            v-model="form.sales_person_id"
            placeholder="Выберете вашего менеджера"
            class="full-width"
            :loading="loadingSalesPersons"
          >
            <el-option
              v-for="person in salesPersons"
              :key="person.id"
              :label="person.id"
              :value="person.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            native-type="submit"
            :loading="loading"
            class="submit-button"
          >
            {{ loading ? 'Обработка...' : (isLogin ? 'Вход' : 'Создать аккаунт') }}
          </el-button>
        </el-form-item>
      </el-form>

      <div class="form-footer">
        <el-divider>
          <span class="divider-text">or</span>
        </el-divider>
        <p class="switch-form">
          {{ isLogin ? "Нет аккаунта?" : "Уже есть аккаунт?" }}
          <el-button link @click="toggleLoginMode">
            {{ isLogin ? 'Создать аккаунт' : 'Вход' }}
          </el-button>
        </p>
      </div>
    </el-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Message, Lock, Connection } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import { SalesPerson, ExchangeRate } from '@/store/types'
import { getCurrencyFlagUrl } from '@/utils/currency'

export default defineComponent({
  name: 'LoginView',
  setup() {
    const store = useStore()
    const router = useRouter()
    const formRef = ref<FormInstance>()
    const isLogin = ref(true)
    const loading = ref(false)
    const loadingSalesPersons = ref(false)
    const salesPersons = ref<SalesPerson[]>([])
    const exchangeRates = ref<ExchangeRate[]>([])
    const loadingRates = ref(false)

    const form = reactive({
      email: '',
      password: '',
      sales_person_id: 0 as number | null
    })

    const fetchSalesPersons = async () => {
      if (!isLogin.value) {
        loadingSalesPersons.value = true
        try {
          await store.dispatch('auth/fetchAllSalesPersons')
          salesPersons.value = store.state.auth.salesPersons

          salesPersons.value.unshift(
            {
              id: 0,
              name: 'Без менеджера',
              last_name: '',
              confirm_signed_documents_flag: false
            }
          )
        } catch (err) {
          ElMessage.error('Не удалось загрузить список менеджеров')
        } finally {
          loadingSalesPersons.value = false
        }
      }
    }

    const fetchExchangeRates = async () => {
      loadingRates.value = true
      try {
        const rates = await store.dispatch('fetchExchangeRates')
        exchangeRates.value = rates
      } catch (err) {
        ElMessage.error('Не удалось загрузить курсы валют')
      } finally {
        loadingRates.value = false
      }
    }

    const toggleLoginMode = () => {
      isLogin.value = !isLogin.value
      if (!isLogin.value) {
        fetchSalesPersons()
      }
    }

    onMounted(() => {
      if (!isLogin.value) {
        fetchSalesPersons()
      }
      fetchExchangeRates()
    })

    watch(isLogin, (newValue) => {
      if (!newValue) {
        fetchSalesPersons()
      }
    })

    const handleSubmit = async () => {
      if (!formRef.value) return
      
      await formRef.value.validate(async (valid) => {
        if (!valid) return

        loading.value = true
        try {
          if (isLogin.value) {
            await store.dispatch('auth/login', {
              email: form.email,
              password: form.password
            })
          } else {
            await store.dispatch('auth/signup', {
              email: form.email,
              password: form.password,
              sales_person_id: form.sales_person_id
            })
          }
          router.push('/')
        } catch (err) {
          ElMessage.error(err instanceof Error ? err.message : 'Произошла ошибка')
        } finally {
          loading.value = false
        }
      })
    }

    return {
      formRef,
      form,
      isLogin,
      loading,
      loadingSalesPersons,
      salesPersons,
      handleSubmit,
      toggleLoginMode,
      Message,
      Lock,
      Connection,
      exchangeRates,
      loadingRates,
      getCurrencyFlagUrl
    }
  }
})
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.auth-form {
  width: 100%;
  max-width: 420px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.card-header {
  text-align: center;
  padding: 0 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
  font-weight: 600;
}

.subtitle {
  margin: 16px 0 0;
  color: #909399;
  font-size: 14px;
}

.submit-button {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  margin-top: 8px;
}

.form-footer {
  margin-top: 24px;
}

.divider-text {
  text-wrap: nowrap;
  color: #909399;
  font-size: 14px;
  padding: 0 12px;
}

.switch-form {
  text-align: center;
  margin: 16px 0 0;
  color: #606266;
  font-size: 14px;
}

.exchange-rates {
  margin-top: 16px;
}

.rates-loading {
  padding: 16px;
}

.rates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.rate-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  background: #f5f7fa;
  border-radius: 6px;
}

.currency-flag {
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
}

.currency-code {
  font-weight: 500;
  color: #303133;
}

.rate-value {
  font-weight: 600;
}

.full-width {
  width: 100%;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset;
}

:deep(.el-card__header) {
  padding: 24px 20px 12px 24px;
  border-bottom: 1px solid #ebeef5;
}

:deep(.el-card__body) {
  padding: 24px 20px;
}

@media (max-width: 480px) {
  .auth-form {
    max-width: 80%;
  }
  
  .card-header h2 {
    font-size: 20px;
  }
}
</style> 